import * as React from 'react';
import IconButton from '@mui/material/IconButton';
import { ReactComponent as CrossIcon } from '@icons/cross.svg';
import { Box, Stack, SxProps, Typography } from '@mui/material';
import { ModalWrapper, ModalWrapperProps } from './ModalWrapper';
import { buildDataTestIdAttribute } from '@src/utils';

export interface DialogTitleProps {
  children?: React.ReactNode;
  onClose?: () => void;
  sx?: SxProps;
  icon?: React.ReactNode;
  dataTestId?: string;
}

function BootstrapDialogTitle(props: DialogTitleProps) {
  const { children, onClose, sx, icon, dataTestId } = props;

  return (
    <Stack
      direction="row"
      justifyContent="space-between"
      alignItems="center"
      sx={{
        m: 0,
        borderBottom: '1px solid #DFF2F5',
        padding: onClose ? '4px 12px 4px 24px' : '12px 24px',
        ...sx,
      }}
    >
      <Stack direction="row" gap="12px">
        {icon}
        <Typography variant="body18Bold" color="grey.700">
          {children}
        </Typography>
      </Stack>
      {onClose ? (
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            svg: { path: { fill: theme => theme.palette.grey[700] } },
          }}
          {...buildDataTestIdAttribute(dataTestId, 'button-cross')}
        >
          <CrossIcon />
        </IconButton>
      ) : null}
    </Stack>
  );
}

export interface ModalProps {
  open: boolean;
  onCancel?: () => void;
  title: string;
  children: React.ReactNode;
  sxTitle?: SxProps;
  wrapperProps?: Omit<ModalWrapperProps, 'open' | 'onClose' | 'children'>;
  bodySx?: SxProps;
  iconTitle?: React.ReactNode;
}

export const Modal: React.FC<ModalProps> = ({
  onCancel,
  open,
  title,
  children,
  wrapperProps,
  sxTitle,
  iconTitle,
  bodySx,
}) => {
  return (
    <ModalWrapper onClose={onCancel} open={open} {...wrapperProps}>
      <BootstrapDialogTitle
        onClose={onCancel}
        sx={sxTitle}
        icon={iconTitle}
        dataTestId={wrapperProps?.PaperProps?.['data-testid']}
      >
        {title}
      </BootstrapDialogTitle>
      <Box sx={{ padding: '24px', height: '100%', ...bodySx }}>{children}</Box>
    </ModalWrapper>
  );
};
